import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {translations} from '../../../../../i18n/translations';
import {
  ExportCarePlanGroupDataProcessor,
  RowData,
} from '../base/export-care-plan-group-data-processor';
import {ExportCarePlanEntry, ExportCarePlanTableColumnStyle} from '../base/export-care-plan-types';

export class CommonBadlXlsxDataProcessor extends ExportCarePlanGroupDataProcessor {
  private _additionalEntries: {id: string | string[]}[] = [];
  private activeAdditionalEntries: {id: string | string[]}[] = [];

  protected get additionalEntries() {
    return this._additionalEntries;
  }

  protected set additionalEntries(entries: {id: string | string[]}[]) {
    this._additionalEntries = entries;
    this.activeAdditionalEntries = entries.filter((additionalEntry) =>
      Boolean(this.getAdditionalEntryName(additionalEntry))
    );
  }

  get additionalEntry() {
    // Hack hardcoded ids: these entries don't have a feature set, so we look by id.
    return {
      positioning: {
        id: '64a75b51b67f03ded5309993',
      },
      mobility: {
        id: '698c6824f5aabb0d32d2e90c',
      },
      requiredAssistance: {
        id: '698c7026f5aabb0d32d2e915',
      },
    };
  }

  override async resolveTaskHeaders(firstRowData: RowData): Promise<string[]> {
    const headers = firstRowData.entries
      .filter(
        (entry) =>
          !this.activeAdditionalEntries.some((additionalEntry) =>
            this.additionalEntryExists(entry, additionalEntry)
          )
      )
      .map((e) => e.name);
    const endDateHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_date.label
    );
    const endHourHeader = this.t(
      translations.activity_type_posology_schedule_form.fields.end_hour.label
    );

    const headersOrdered = this.reorderCells(headers, endDateHeader, endHourHeader);

    return [
      ...headersOrdered.slice(0, -1),
      ...this.activeAdditionalEntries.map((additionalEntry) =>
        this.getAdditionalEntryName(additionalEntry)
      ),
      ...headersOrdered.slice(-1),
    ];
  }

  override resolveTaskRow(rowData: RowData): string[] {
    const cells: string[] = [];
    let endDateCell: string = '';
    let endHourCell: string = '';

    for (const entry of rowData.entries) {
      switch (entry.feature) {
        case CarePlanConfigurationFeature.Schedule: {
          const {periodicity, endDate, endHour} = this.resolveEntryCellSchedule(entry);
          cells.push(periodicity);
          endDateCell = endDate;
          endHourCell = endHour;
          break;
        }

        default:
          if (
            this.activeAdditionalEntries.some((additionalEntry) =>
              this.additionalEntryExists(entry, additionalEntry)
            )
          ) {
            // Hack*: Skip these, not all rows have them and we add them later bellow in the correct order.
            break;
          }
          cells.push(this.resolveEntryCell(entry));
          break;
      }
    }

    // HACK*: Additional cells
    const additionalCells: string[] = [];
    this.activeAdditionalEntries.forEach((additionalEntry) => {
      const entry = rowData.entries.find((e) => this.additionalEntryExists(e, additionalEntry));
      const value = this.getEntryValue(entry);
      additionalCells.push(value);
    });

    const cellsOrdered = this.reorderCells(cells, endDateCell, endHourCell);

    return [...cellsOrdered.slice(0, -1), ...additionalCells, ...cellsOrdered.slice(-1)];
  }

  override resolveTasksColumnStyles(
    taskHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    return taskHeaders.map((header, idx) => {
      return {cellWidth: idx < 6 || idx === taskHeaders.length - 1 ? 15 : 30};
    });
  }

  private additionalEntryExists(
    entry: ExportCarePlanEntry,
    additionalEntry: {id: string | string[]}
  ): boolean {
    return Array.isArray(additionalEntry.id)
      ? additionalEntry.id.includes(entry.id)
      : additionalEntry.id === entry.id;
  }

  private getAdditionalEntryName(additionalEntry: {id: string | string[]}): string {
    return this.getTaskEntryById(additionalEntry.id)?.name ?? '';
  }
}
